import React, { useEffect, useRef, useState } from 'react';
import * as Yup from "yup";
import { Form, Formik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../../components/BreadCrumbs';
import InputField from '../../../components/inputFields/InputField';
import FormikDropdown from '../../../components/inputFields/FormikDropDown';
import Button from '../../../components/buttons/Button';
import StyledHeadingText from '../../../components/Fonts/StyledHeadingText';
import { addHudPatientPackageInvoice } from '../../../services/hud/hud_patients';

function AddPatientPackageInvoice() {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState({});

    const handleFormSubmit = async (values, { setSubmitting }) => {
        try {
            const formData = new FormData();
            formData.append("package", rowData.package_id);
            formData.append("amount", values.amount);
            formData.append("mode", values.mode);
            formData.append("transaction_no", values.transaction_number);
            formData.append("remark", values.remark);
            const response = await addHudPatientPackageInvoice(formData);

            if (response?.status === 201) {
                toast.success("Invoice Generated successfully!");
                navigate(-1);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    const handleDiscardChanges = (resetForm) => {
        resetForm(); // Reset the form
        navigate(-1);
    };

    useEffect(() => {
        const patientData = location.state;
        if (patientData) {
            setRowData(patientData);
        } else {
            toast.error("Error while looking for patient");
            navigate(-1);
        }
    }, [location.state]);

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/invoice' },
                { name: 'Patients', link: '/hud-patients' },
                { name: 'Patient Packages' },
                { name: 'Add Invoice' },
            ]} />
            <div className="mt-8">
                <Formik
                    initialValues={{
                        amount: "",
                        mode: "UPI",
                        transaction_number: "",
                        remark: "",
                    }}
                    validationSchema={Yup.object({
                        amount: Yup.number()
                            .required("Please enter an amount")
                            .positive("Amount must be positive")
                            .integer("Amount must be an integer")
                            .min(0, "Minimum should be 0")
                            .max(rowData.remaining_amount || 0,
                                `Max ₹${(rowData.remaining_amount || 0)} is allowed`
                            ),
                        mode: Yup.string().required("Please select a payment mode"),
                    })}
                    onSubmit={handleFormSubmit}
                >
                    {({ isSubmitting, setFieldValue, resetForm, values }) => (
                        <Form>
                            <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 flex flex-col gap-4">
                                <StyledHeadingText text={"Package: " + rowData.package_name + " | Rem. Am: ₹" + rowData.remaining_amount} />
                                <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-0.5">
                                    <InputField
                                        label="Amount"
                                        name="amount"
                                        type="text"
                                        placeholder="Enter Amount"
                                        required
                                    />
                                    <FormikDropdown
                                        label="Mode"
                                        name="mode"
                                        placeholder="Select Mode"
                                        required={true}
                                        options={[
                                            { label: "NEFT", value: "NEFT" },
                                            { label: "Cash", value: "Cash" },
                                            { label: "UPI", value: "UPI" },
                                            { label: "Card", value: "Card" },
                                        ]}
                                    />
                                    {
                                        values.mode === "UPI" || values.mode === "Card" ?
                                            <InputField
                                                label={values.mode === "UPI" ? "UPI Number" : "Card Number"}
                                                name="transaction_number"
                                                placeholder={values.mode === "UPI" ? "Enter UPI Number" : "Enter Card Number"}
                                            /> : <></>
                                    }
                                    <InputField
                                        label="Remark"
                                        name="remark"
                                        type="text"
                                        placeholder="You can add a remark here"
                                    />
                                </div>
                            </div>
                            <div className="py-5 flex justify-end gap-6">
                                <div>
                                    <Button
                                        type="button"
                                        className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                        onClick={() => handleDiscardChanges(resetForm)}
                                        disabled={isSubmitting}
                                    >
                                        Discard Changes
                                    </Button>
                                </div>
                                <div>
                                    <Button
                                        type="submit"
                                        className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
}

export default AddPatientPackageInvoice;
